import { Controller } from "@hotwired/stimulus"
import { get, patch } from "@rails/request.js"

interface QueueItem {
  itemId: string
  containerId?: string | null
  title: string
  creatorName: string
  durationSeconds: number
  muxPlaybackId: string | null
  fileUrl?: string | null
  sourceUrl?: string | null
  imageUrl: string
  unplayable?: boolean
  preview?: boolean
}

interface QueueState {
  items: QueueItem[]
  currentIndex: number
  source: { type: string; id: string }
}

interface ServerState {
  items: QueueItem[]
  current_index: number
  current_time: number
  source_type: string | null
  source_id: string | null
}

export default class extends Controller {
  static values = {
    state: { type: Object, default: {} },
  }

  declare stateValue: ServerState | Record<string, never>

  private queue: QueueState = {
    items: [],
    currentIndex: 0,
    source: { type: "single", id: "" },
  }

  connect() {
    this.restoreFromValue()
  }

  // Saved listening position from the server state — consumed by the
  // persistent player when it mounts the engine lazily.
  restoredTime = 0

  get currentItem(): QueueItem | undefined {
    return this.queue.items[this.queue.currentIndex]
  }

  get items(): QueueItem[] {
    return this.queue.items
  }

  get currentIndex(): number {
    return this.queue.currentIndex
  }

  get hasNext(): boolean {
    return this.queue.currentIndex < this.queue.items.length - 1
  }

  get hasPrevious(): boolean {
    return this.queue.currentIndex > 0
  }

  enqueue(
    itemsJson: string,
    startIndex: number,
    sourceType: string,
    sourceId: string,
  ) {
    const items = JSON.parse(itemsJson) as QueueItem[]
    const replaced = !this.hasSameItems(items)
    this.queue = {
      items,
      currentIndex: startIndex,
      source: { type: sourceType, id: sourceId },
    }
    this.dispatch("change", { detail: { item: this.currentItem, replaced } })
    void this.saveToServer()
    void this.loadToken()
  }

  next() {
    if (!this.hasNext) return
    this.queue.currentIndex++
    this.dispatch("change", { detail: { item: this.currentItem } })
    void this.saveToServer()
    void this.loadToken()
  }

  previous(currentTime: number) {
    if (currentTime > 3) {
      this.dispatch("restart")
      return
    }
    if (!this.hasPrevious) return
    this.queue.currentIndex--
    this.dispatch("change", { detail: { item: this.currentItem } })
    void this.saveToServer()
    void this.loadToken()
  }

  advance() {
    if (this.hasNext) {
      this.queue.currentIndex++
      this.dispatch("change", { detail: { item: this.currentItem } })
      void this.saveToServer()
      void this.loadToken()
    } else {
      this.dispatch("ended")
    }
  }

  get upNextItems(): QueueItem[] {
    return this.queue.items.slice(this.queue.currentIndex + 1)
  }

  skipTo(index: number) {
    if (index < 0 || index >= this.queue.items.length) return
    this.queue.currentIndex = index
    this.dispatch("change", { detail: { item: this.currentItem } })
    void this.saveToServer()
    void this.loadToken()
  }

  syncTo(itemId: string, position: number) {
    const index = this.queue.items.findIndex((item) => item.itemId === itemId)
    if (index < 0 || index === this.queue.currentIndex) return

    this.queue.currentIndex = index
    this.dispatch("change", {
      detail: { item: this.currentItem, playIntent: false },
    })
    this.savePosition(position)
  }

  clearUpcoming() {
    this.queue.items = this.queue.items.slice(0, this.queue.currentIndex + 1)
    // Trimming what's next is the one change that isn't a play intent.
    this.dispatch("change", {
      detail: { item: this.currentItem, playIntent: false, replaced: true },
    })
    void this.saveToServer()
  }

  skipUnplayable() {
    for (
      let i = this.queue.currentIndex + 1;
      i < this.queue.items.length;
      i++
    ) {
      if (!this.queue.items[i].unplayable) {
        this.queue.currentIndex = i
        this.dispatch("change", { detail: { item: this.currentItem } })
        void this.saveToServer()
        void this.loadToken()
        return
      }
    }
    this.dispatch("ended")
  }

  savePosition(currentTime: number) {
    void patch("/playback_queue", {
      body: JSON.stringify({
        playback_queue: {
          current_time: currentTime,
          current_index: this.queue.currentIndex,
        },
      }),
      contentType: "application/json",
    })
  }

  // Restored queues (full reload / fresh session) have no mounted
  // engine; the persistent player calls this on the first play intent
  // to fetch a token and mount it.
  requestCurrentToken() {
    void this.loadToken()
  }

  private async loadToken() {
    const item = this.currentItem
    if (!item) return

    // File-backed items (Mux ingest pending / plain uploads) skip the
    // token service and play the attachment directly.
    if (!item.muxPlaybackId && item.fileUrl) {
      this.dispatch("token", {
        detail: {
          playbackId: null,
          token: null,
          fileUrl: item.fileUrl,
          preview: false,
          previewDuration: null,
        },
      })
      return
    }

    let response
    try {
      response = await get(`/playback_tokens/${item.itemId}`, {
        contentType: "application/json",
      })
    } catch {
      return
    }

    if (response.statusCode === 403) {
      item.unplayable = true
      this.skipUnplayable()
      return
    }

    if (!response.ok) return

    const data = (await response.json) as {
      mux_playback_id?: string | null
      playback_token?: string | null
      file_url?: string | null
      preview?: boolean
      preview_duration?: number
    }
    // Preview-then-advance: unowned items come back with their preview clip,
    // a file of its own rather than a token against the full asset (#1580).
    // The clip plays, ends, and the normal ended->next auto-advance moves the
    // queue along.
    item.preview = Boolean(data.preview)
    this.dispatch("token", {
      detail: {
        playbackId: data.mux_playback_id ?? null,
        token: data.playback_token ?? null,
        fileUrl: data.file_url ?? null,
        preview: item.preview,
        previewDuration: data.preview_duration ?? null,
      },
    })
  }

  private hasSameItems(items: QueueItem[]): boolean {
    return (
      this.queue.items.length > 0 &&
      items.length === this.queue.items.length &&
      items.every(
        (item, index) => item.itemId === this.queue.items[index].itemId,
      )
    )
  }

  private async saveToServer() {
    await patch("/playback_queue", {
      body: JSON.stringify({
        playback_queue: {
          items: this.queue.items,
          current_index: this.queue.currentIndex,
          source_type: this.queue.source.type,
          source_id: this.queue.source.id,
        },
      }),
      contentType: "application/json",
    })
  }

  private restoreFromValue() {
    const data = this.stateValue
    if (!("items" in data) || !data.items || data.items.length === 0) return

    this.queue = {
      items: data.items,
      currentIndex: data.current_index,
      source: {
        type: data.source_type ?? "single",
        id: data.source_id ?? "",
      },
    }
    this.restoredTime = data.current_time ?? 0
    this.dispatch("restore", {
      detail: { item: this.currentItem, currentTime: data.current_time },
    })
  }

  stateValueChanged() {
    this.restoreFromValue()
  }
}
